import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { Marquee } from 'atoms/container/Marquee';
import { Box } from 'atoms/icons/Box';
import { Cards } from 'atoms/icons/Cards';
import { Team } from 'atoms/icons/Team';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyL, BrandM, BrandXXL } from 'atoms/typography';
import { CardImg } from 'components/card/CardImg';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { laptopAndAbove } from 'style/mediaQuery';

import bg from './assets/bg.webp';
import type { HowToPlay_card } from '../__generated__/index.graphql';
import type { Hero_card } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({
  center: true,
})`
  background-image: url(${bg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
  width: 100%;
`;

const Container = styled(Vertical).attrs({
  center: true,
  gap: 8,
})`
  padding-top: calc(var(--unit) * 10);
  padding-bottom: calc(var(--unit) * 10);
  width: 100%;
`;

const IconWrapper = styled(Horizontal)`
  margin-right: var(--unit);
`;

const CardContainer = styled(Horizontal).attrs({ gap: 2, center: true })`
  & > * {
    aspect-ratio: var(--card-aspect-ratio);
    max-width: 160px;
  }
`;

const TitleContainer = styled(Vertical).attrs({
  center: true,
  gap: 6,
})`
  width: 100%;
`;

const DescriptionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  @media ${laptopAndAbove} {
    flex-direction: row;
    justify-content: center;
    gap: var(--quadruple-unit);
  }
`;

const heroDescription = defineMessages({
  collect: {
    id: 'HowToPlay.Hero.description.collect',
    defaultMessage: 'Collect <bold>officially licensed sports cards</bold>',
    values: {
      bold: (chunks: ReactNode) => <strong>{chunks}</strong>,
    },
  },
  build: {
    id: 'HowToPlay.Hero.description.build',
    defaultMessage: 'Build your dream teams',
  },
  compete: {
    id: 'HowToPlay.Hero.description.compete',
    defaultMessage: 'Compete for <bold>incredible rewards</bold>',
    values: {
      bold: (chunks: ReactNode) => <strong>{chunks}</strong>,
    },
  },
});

const heroIcons = {
  collect: Cards,
  build: Team,
  compete: Box,
};

export const Hero = ({ cards }: { cards: HowToPlay_card[] }) => {
  const isLaptopAndAbove = useIsLaptop();
  return (
    <Root>
      <Container className="text-center">
        <TitleContainer className="text-center">
          <Vertical gap={3} center>
            <BrandM uppercase>
              <FormattedMessage
                id="HowToPlay.Hero.title"
                defaultMessage="Fantasy sports meets trading cards"
              />
            </BrandM>
            <BrandXXL
              uppercase
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': `linear-gradient(
        45deg,
        #f8d3da,
        #b3a9f4 28%,
        #fbe9fb 54%,
        #4f94fd 100%
      )`,
                } as CSSProperties
              }
            >
              <FormattedMessage
                id="HowToPlay.Hero.subtitle"
                defaultMessage="You're the manager"
              />
            </BrandXXL>
          </Vertical>
          <DescriptionContainer>
            {(
              Object.keys(heroDescription) as Array<
                keyof typeof heroDescription
              >
            ).map(key => {
              const message = heroDescription[key];
              const Icon = heroIcons[key];
              return (
                <Horizontal key={message.id} gap={0} center>
                  <IconWrapper style={{ color: 'var(--c-blue-400)' }}>
                    <Icon />
                  </IconWrapper>
                  <BodyL>
                    <FormattedMessage {...message} />
                  </BodyL>
                </Horizontal>
              );
            })}
          </DescriptionContainer>
        </TitleContainer>
        {!!isLaptopAndAbove && (
          <CardContainer gap={2}>
            {cards.map(card => (
              <CardImg key={card.slug} src={card.pictureUrl} width={160} />
            ))}
          </CardContainer>
        )}
        {!isLaptopAndAbove && (
          <Marquee duration="20s" gap={2}>
            {cards.map(card => (
              <CardImg key={card.slug} src={card.pictureUrl} width={80} />
            ))}
          </Marquee>
        )}
      </Container>
    </Root>
  );
};

Hero.fragments = {
  card: gql`
    fragment Hero_card on Card {
      slug
      pictureUrl
    }
  ` as TypedDocumentNode<Hero_card>,
};
